import { useMemo, useSyncExternalStore } from 'react';
import type { MyTaskDto } from '../../api/tasks';
import { dueDayDiff, dueState, dueStateLabel } from '../ProjectPage/my-task-due';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './MyTasksNextUpTable.css';

/**
 * NextUpTable (2026-09-01) — 요약 서랍을 **표 하나로**. 신규 파일.
 *
 * 무엇을 대체했나: 나란히 서 있던 카드 둘 — `DueFlow`(마감 흐름 막대 차트)와
 * `NextUpList`(1~4순위 목록). 둘 다 지우지 않고 이 표로 갈아 끼우기만 한다(소스 회귀 금지).
 *
 * 왜 바꿨나 — 화면을 채우는 것에 비해 말하는 내용이 적었다:
 *   ① 마감 흐름은 16칸짜리 차트인데 값이 붙는 칸은 보통 둘뿐이었다(지연 · 미정). 나머지
 *      열넷은 높이 2px 짜리 회색 선이라, 차트 폭 전체가 사실상 **두 숫자**를 위해 쓰였다.
 *      게다가 그 '지연' 은 툴바 칩과 오른쪽 목록의 '28일 지연' 에 이미 두 번 더 있었다.
 *   ② 순위 목록은 11건을 보여 주려고 머리 4줄 · 설명 4줄 · 범례 1줄을 썼고, 정작 업무
 *      이름은 순위마다 3건까지만 나와 나머지는 '+n건 더' 로 접혔다. 요약을 펴는 이유가
 *      "그래서 뭐부터" 인데, 그 답의 절반이 접혀 있었다.
 *
 * 그래서 **같은 정보를 표 하나 + 한 줄 둘**로 편다. 분류·정렬 규칙은 NextUpList 와 똑같이
 * 두므로(아래 주석 참조) 숫자가 달라지지 않는다:
 *   - 표      순위 · 업무 · 프로젝트 · 기한. 한 줄이 업무 하나. 위에서 아래로 읽으면 그게 순서.
 *   - 윗줄    마감 흐름이 하던 말 — 지연 n · 오늘 마감 n · 2주 내 n · 미정 n
 *   - 아랫줄  순위별 건수 — 1 지금 당장 4 · 2 오늘 중 착수 0 · … ('0건' 도 그대로 남는다)
 *
 * 2026-09-01 (2차) — **표는 그대로 두고 칸 안에** 시각 정보를 넣는다. 셋 다 줄 높이를
 * 늘리지 않는다 — 표를 다시 차트로 되돌리면 처음 문제로 돌아가기 때문이다:
 *   ① 순위 레일   행 왼쪽 3px 색 띠. 훑으면 1순위 덩어리가 어디까지인지 눈에 잡힌다.
 *   ② 기한 막대   기한 글자 아래 깔리는 막대. **길수록 급하다** 로 규칙을 하나만 쓴다 —
 *                 지연은 밀린 날수에 비례(빨강), 남은 일은 가까울수록 길게(주황·회색).
 *                 '28일 지연' 과 '3일 지연' 이 같은 크기로 보이던 것을 가른다.
 *   ③ 흐름 막대   윗줄 네 숫자를 얇은 누적 막대 한 줄로. 숫자는 그대로 옆에 남는다.
 *
 * ⚠ 진행률 막대(행 아래 경계선을 초록으로 채우던 것)는 2026-09-01 사용자 지시로 뺐다.
 *   되살리지 말 것 — 표 아래에 초록 선이 깔리는 것이 원하는 그림이 아니다.
 *
 * 순위 규칙은 4분면·목록과 **동일**하다(기준을 새로 만들면 화면의 다른 숫자와 어긋난다):
 *   중요 = `priority === 'HIGH'`  ·  급함 = 마감이 오늘 포함 2일 안 (지연 포함)
 *     1 중요 O·급함 O   2 중요 O·급함 X   3 중요 X·급함 O   4 중요 X·급함 X
 *   2와 3의 순서는 의도한 것이다 — 급한 남의 일에 하루를 쓰고 중요한 내 일을 미루는 것이
 *   이 요약이 막으려는 실패라, 중요를 급함보다 위에 둔다.
 *
 * 완료(DONE)는 세지 않는다 — 끝난 일은 앞으로의 부담이 아니다.
 * 데이터는 받아 둔 목록에서 만든다 — 서버 호출 0.
 */

/** '급함' 의 경계. 오늘·내일·모레까지. NextUpList 와 같은 값. */
const URGENT_DAYS = 2;

/** '2주 내' 로 세는 구간. 종전 마감 흐름 차트가 그리던 폭과 같다. */
const FLOW_DAYS = 14;

/** 표에 세우는 줄 수의 상한. 넘으면 '+n건 더' 한 줄로 접는다 — 요약이 표를 밀어내면 안 된다. */
const MAX_ROWS = 10;

/** 마감이 없거나 못 읽는 업무를 맨 뒤로 보내기 위한 정렬 키. */
const NO_DUE = 9999;

/**
 * 기한 막대의 자 — 지연은 30일, 남은 일은 14일에서 꽉 찬다.
 * 30일을 넘게 밀린 일끼리는 더 가르지 않는다(막대가 길다는 사실이 이미 답이다).
 */
const BAR_OVERDUE_CAP = 30;
const BAR_LEFT_CAP = 14;

/** 아무리 급해도 최소 이만큼은 그린다 — 1일 지연이 선 하나로 사라지지 않게. */
const BAR_MIN = 0.14;

/** 흐름 누적 막대의 칸 — 급한 순서대로 왼쪽부터. 색은 화면의 기존 규칙(지연=빨강·임박=주황). */
const FLOW_SEGS = [
  { key: 'over', tone: 'over' },
  { key: 'today', tone: 'today' },
  { key: 'soon', tone: 'soon' },
  { key: 'none', tone: 'none' },
] as const;

type RankOrder = 1 | 2 | 3 | 4;

interface Row {
  task: MyTaskDto;
  rank: RankOrder;
}

function isUrgent(t: MyTaskDto, now: number): boolean {
  if (!t.dueDate) return false;
  const diff = dueDayDiff(t.dueDate, now);
  if (Number.isNaN(diff)) return false;
  return diff <= URGENT_DAYS;
}

/** 업무 하나의 순위. NextUpList 의 분류와 글자 그대로 같은 규칙이다. */
export function rankOf(t: MyTaskDto, now: number): RankOrder {
  const important = t.priority === 'HIGH';
  const urgent = isUrgent(t, now);
  if (important) return urgent ? 1 : 2;
  return urgent ? 3 : 4;
}

/**
 * 기한 막대의 길이(0~1)와 색. 규칙은 하나 — **길수록 급하다.**
 *   지연  밀린 날수에 비례(30일에서 꽉 참) · 빨강
 *   오늘/내일  거의 꽉 참 · 주황
 *   그 밖  마감이 가까울수록 길게(2주에서 0) · 회색
 *   마감 미정 · 2주 밖  안 그린다 — 지금 손쓸 구간이 아니다.
 */
function dueBar(diff: number): { w: number; tone: 'over' | 'soon' | 'far' } | null {
  if (diff === NO_DUE) return null;
  if (diff < 0) {
    const w = Math.min(Math.abs(diff), BAR_OVERDUE_CAP) / BAR_OVERDUE_CAP;
    return { w: Math.max(w, BAR_MIN), tone: 'over' };
  }
  if (diff <= 1) return { w: Math.max((BAR_LEFT_CAP - diff) / BAR_LEFT_CAP, BAR_MIN), tone: 'soon' };
  if (diff >= BAR_LEFT_CAP) return null;
  return { w: Math.max((BAR_LEFT_CAP - diff) / BAR_LEFT_CAP, BAR_MIN), tone: 'far' };
}

/** 정렬 키 — 지연이 맨 앞, 그다음 마감 가까운 순, 마감 미정은 맨 뒤. */
function dueKey(t: MyTaskDto, now: number): number {
  if (!t.dueDate) return NO_DUE;
  const diff = dueDayDiff(t.dueDate, now);
  return Number.isNaN(diff) ? NO_DUE : diff;
}

export function NextUpTable({ items, onPickTask, now = Date.now() }: {
  items: MyTaskDto[];
  onPickTask?: (taskId: string) => void;
  /** 테스트에서 '오늘' 을 고정하기 위한 주입점. 화면에서는 안 넘긴다. */
  now?: number;
}) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);

  const { rows, counts, flow } = useMemo(() => {
    const open = items.filter((t) => t.status !== 'DONE');

    const all: Row[] = open.map((t) => ({ task: t, rank: rankOf(t, now) }));
    // 순위가 1차 키, 기한이 2차 키. 표를 위에서 아래로 읽으면 그것이 곧 할 순서다.
    all.sort((a, b) => a.rank - b.rank || dueKey(a.task, now) - dueKey(b.task, now));

    const c: Record<RankOrder, number> = { 1: 0, 2: 0, 3: 0, 4: 0 };
    for (const r of all) c[r.rank] += 1;

    // 종전 마감 흐름 차트가 세던 것과 같은 네 덩어리.
    let over = 0; let today = 0; let soon = 0; let none = 0;
    for (const t of open) {
      const d = dueKey(t, now);
      if (d === NO_DUE) { none += 1; continue; }
      if (d < 0) { over += 1; continue; }
      if (d === 0) { today += 1; continue; }
      if (d < FLOW_DAYS) soon += 1;
    }

    return { rows: all, counts: c, flow: { over, today, soon, none } };
  }, [items, now]);

  const shown = rows.slice(0, MAX_ROWS);
  const rest = rows.length - shown.length;

  const rankLabel: Record<RankOrder, string> = {
    1: tProject('project.mytask.nextup_r1'),
    2: tProject('project.mytask.nextup_r2'),
    3: tProject('project.mytask.nextup_r3'),
    4: tProject('project.mytask.nextup_r4'),
  };

  return (
    <div className="mti-card mtt-card">
      <h3 className="mti-cardtitle">{tProject('project.mytask.nextup_title')}</h3>

      {rows.length === 0 ? (
        <p className="mti-cardempty" role="status">{tProject('project.mytask.insights_empty')}</p>
      ) : (
        <>
          {/*
            마감 흐름이 차트로 하던 말 — 값은 그대로고 한 줄이 됐다.
            2026-09-01(2차) — 그 한 줄에 **얇은 누적 막대**를 붙인다. 숫자는 그대로 남고,
            막대는 "지연이 전체의 얼마나 되나" 를 읽는 데만 쓴다(높이 6px, 줄은 그대로 하나).
          */}
          <div className="mtt-flowline">
            <span
              className="mtt-flowbar"
              role="img"
              aria-label={tProject('project.mytask.nextup_table_flow', {
                over: String(flow.over),
                today: String(flow.today),
                soon: String(flow.soon),
                none: String(flow.none),
              })}
            >
              {FLOW_SEGS.map(({ key, tone }) => {
                const n = flow[key];
                if (n === 0) return null;
                return (
                  <span
                    key={key}
                    className={`mtt-flowseg mtt-flowseg--${tone}`}
                    style={{ flexGrow: n }}
                  />
                );
              })}
            </span>
            <p className="mtt-flow" aria-hidden>
              {tProject('project.mytask.nextup_table_flow', {
                over: String(flow.over),
                today: String(flow.today),
                soon: String(flow.soon),
                none: String(flow.none),
              })}
            </p>
          </div>

          <div className="mtt-scroll">
            <table className="mtt-table">
              <thead>
                <tr>
                  <th scope="col" className="mtt-th-rank">{tProject('project.mytask.nextup_table_rank')}</th>
                  <th scope="col">{tProject('project.mytask.nextup_table_task')}</th>
                  <th scope="col" className="mtt-th-proj">{tProject('project.mytask.nextup_table_project')}</th>
                  <th scope="col" className="mtt-th-due">{tProject('project.mytask.nextup_table_due')}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map(({ task: t, rank }) => {
                  const st = dueState(t.dueDate, t.status, now);
                  const over = st === 'overdue';
                  const bar = dueBar(dueKey(t, now));
                  return (
                    <tr className={`mtt-row mtt-row--r${rank}`} key={t.id}>
                      <td className="mtt-td-rank">
                        {/* 배지는 줄마다 둔다 — 순위 머리줄을 따로 세우지 않으려는 것이고,
                            그래야 한 줄만 떼어 봐도 그 업무가 몇 순위인지 알 수 있다. */}
                        <span
                          className={`mtt-badge mtt-badge--r${rank}`}
                          title={rankLabel[rank]}
                        >
                          {rank}
                        </span>
                      </td>
                      <td className="mtt-td-task">
                        <button
                          type="button"
                          className="mtt-title"
                          data-tip={t.title}
                          aria-label={tProject('project.mytask.detail_aria', { name: t.title })}
                          onClick={() => onPickTask?.(t.id)}
                        >
                          {t.title}
                        </button>
                      </td>
                      <td className="mtt-td-proj" title={t.project?.name ?? ''}>
                        {t.project?.name ?? '—'}
                      </td>
                      <td className={`mtt-td-due${over ? ' mtt-td-due--over' : ''}`}>
                        <span className="mtt-duetext">{st ? dueStateLabel(st, t.dueDate, now) : '—'}</span>
                        {/* 급한 정도 — 길수록 급하다. 글자 아래 깔려 줄 높이를 늘리지 않는다. */}
                        {bar && (
                          <span
                            className={`mtt-duebar mtt-duebar--${bar.tone}`}
                            style={{ width: `${Math.round(bar.w * 100)}%` }}
                            aria-hidden
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
                {rest > 0 && (
                  <tr className="mtt-row mtt-row--more">
                    <td colSpan={4}>{tProject('project.mytask.nextup_more', { n: String(rest) })}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/*
            순위별 건수 — 종전에는 순위마다 머리줄과 설명 한 문장을 따로 세워 넉 줄을 썼다.
            '2순위 0건' 이 그 자체로 좋은 소식이라는 점은 여기서 0 을 그대로 적어 지킨다.
          */}
          <p className="mtt-legend">
            {([1, 2, 3, 4] as RankOrder[]).map((r) => (
              <span className="mtt-legenditem" key={r}>
                <span className={`mtt-badge mtt-badge--r${r}`} aria-hidden>{r}</span>
                {rankLabel[r]}
                <b className={counts[r] === 0 ? 'mtt-legendn mtt-legendn--zero' : 'mtt-legendn'}>
                  {counts[r]}
                </b>
              </span>
            ))}
            <span className="mtt-legendhint">{tProject('project.mytask.nextup_table_click')}</span>
          </p>
        </>
      )}
    </div>
  );
}
